import React from 'react';
import { useNavigate, Link } from 'react-router-dom';

/**
 * Component for displaying users and their applications as part of the system settings
 * 
 * @param {Object} props
 * @param {Array} props.users - List of users with their applications count
 * @param {boolean} props.loading - Loading state
 * @param {Object} props.selectedUser - Currently selected user
 * @param {Array} props.selectedUserApplications - Applications of the selected user
 * @param {boolean} props.applicationsLoading - Loading state for applications
 * @param {Function} props.onUserSelect - Handler for user selection
 * @param {Function} props.onModalClose - Handler for modal close
 * @returns {JSX.Element}
 */
const UsersManagementSection = ({ 
  users = [], 
  loading = false,
  selectedUser,
  selectedUserApplications = [],
  applicationsLoading = false,
  onUserSelect,
  onModalClose
}) => {
  const navigate = useNavigate();
  
  console.log('UsersManagementSection props:', {
    usersCount: users.length,
    loading,
    selectedUser,
    applicationsCount: selectedUserApplications.length,
    applicationsLoading
  });

  const handleUserClick = (user) => {
    console.log('User row clicked:', user);
    if (!user || !user.uid) {
      console.error('Invalid user object or missing uid:', user);
      alert('Could not open user details: Invalid user data');
      return;
    }
    navigate(`/system-settings/user/${user.uid}`);
  };

  return (
    <div className="bg-gray-900 rounded-xl shadow-xl p-6 mb-8 border border-gray-800">
      <h2 className="text-xl font-semibold mb-4 text-blue-400 text-left">User Management</h2>
      <p className="text-gray-400 mb-6 text-left">View registered users and their application statistics</p>
      
      {/* Loading state */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Users list */}
      <div className="overflow-x-auto rounded-lg ring-1 ring-gray-700">
        <table className="min-w-full divide-y divide-gray-700">
          <thead>
            <tr>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">User Name</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Email</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Applications Count</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Registration Date</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan="5" className="p-4 text-center text-gray-400">
                  No users found in the system.
                </td>
              </tr>
            )}
            
            {users.map((user, index) => (
              <tr 
                key={user.uid} 
                className="bg-gray-900 hover:bg-gray-800 transition-colors duration-150 cursor-pointer"
                onClick={() => {
                  console.log('User row clicked:', user);
                  handleUserClick(user);
                }}
              >
                <td className="py-3 px-4">{user.displayName || 'Not specified'}</td>
                <td className="py-3 px-4">{user.email}</td>
                <td className="py-3 px-4 text-center">
                  <span className="inline-flex items-center justify-center px-3 py-1 bg-blue-900/30 text-blue-300 rounded-full">
                    {user.applicationsCount || 0}
                  </span>
                </td>
                <td className="py-3 px-4">
                  {user.createdAt 
                    ? (user.createdAt.seconds 
                        ? new Date(user.createdAt.seconds * 1000).toLocaleString() 
                        : typeof user.createdAt === 'number' 
                          ? new Date(user.createdAt).toLocaleString()
                          : 'Not available')
                    : 'Not available'
                  }
                </td>
                <td className="py-3 px-4">
                  <span 
                    className={user.isActive ? 'text-green-300' : 'text-red-300'}
                  >
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Applications Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl shadow-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-blue-400">
                Applications for {selectedUser.displayName || selectedUser.email}
              </h3>
              <button
                onClick={() => {
                  console.log('Modal close button clicked');
                  onModalClose();
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Applications loading state */}
            {applicationsLoading ? (
              <div className="flex justify-center my-8">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : selectedUserApplications.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedUserApplications.map((app, index) => (
                  <div key={app.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h4 className="text-lg font-medium text-white mb-2">{app.name}</h4>
                    <div className="space-y-2">
                      <p className="text-gray-300">
                        <span className="text-gray-400">Status:</span>{' '}
                        <span className={
                          app.status === 'completed' ? 'text-green-400' : 
                          app.status === 'analyzing' ? 'text-yellow-400' : 
                          app.status === 'uploaded' ? 'text-blue-400' : 
                          'text-red-400'
                        }>
                          {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                        </span>
                      </p>
                      <p className="text-gray-300">
                        <span className="text-gray-400">Uploaded:</span>{' '}
                        {app.createdAt 
                          ? (typeof app.createdAt.toDate === 'function' 
                              ? new Date(app.createdAt.toDate()).toLocaleString() 
                              : new Date(app.createdAt).toLocaleString())
                          : 'Not available'
                        }
                      </p>
                      <p className="text-gray-300">
                        <span className="text-gray-400">Last Updated:</span>{' '}
                        {app.updatedAt 
                          ? (typeof app.updatedAt.toDate === 'function' 
                              ? new Date(app.updatedAt.toDate()).toLocaleString() 
                              : new Date(app.updatedAt).toLocaleString())
                          : 'Not available'
                        }
                      </p>
                      <p className="text-gray-300">
                        <span className="text-gray-400">File Size:</span>{' '}
                        {(app.fileSize / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      <p className="text-gray-300">
                        <span className="text-gray-400">File Type:</span>{' '}
                        {app.fileType.toUpperCase()}
                      </p>
                      {app.fileId && (
                        <Link 
                          to={`/app-analysis/${app.fileId}`}
                          className="inline-block mt-2 text-blue-400 hover:text-blue-300"
                        >
                          View Analysis →
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">
                No applications found for this user.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagementSection; 